import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  // On mount, check if we have a valid session (user clicked the reset link from email)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setError('Link inválido ou expirado. Solicite uma nova recuperação de senha.');
      }
      setChecking(false);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password || password.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(
        updateError.message === 'New password should be different from the old password.'
          ? 'A nova senha deve ser diferente da senha atual.'
          : 'Erro ao redefinir senha. O link pode ter expirado.',
      );
      setLoading(false);
      return;
    }

    setDone(true);
    setLoading(false);
  };

  // Invalid or expired link state
  if (!checking && error && !done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-close-circle-line text-2xl text-red-500"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">Link inválido</h1>
          <p className="text-sm text-foreground-500 mb-6">
            Este link de recuperação expirou ou já foi utilizado. Solicite um novo para continuar.
          </p>
          <button
            onClick={() => navigate('/esqueci-senha')}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Solicitar novo link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-10">
          <span className="font-semibold text-2xl tracking-tight text-foreground-900">
            Central Check
          </span>
        </div>

        <h1 className="text-xl font-bold text-foreground-900 mb-2 text-center">Redefinir senha</h1>
        <p className="text-sm text-foreground-500 mb-8 text-center">
          {done ? 'Senha redefinida com sucesso!' : 'Escolha uma nova senha para sua conta.'}
        </p>

        {done ? (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl text-green-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900 mb-2">Tudo pronto!</p>
            <p className="text-sm text-foreground-500 mb-6">
              Sua senha foi alterada. Agora você pode acessar com sua nova senha.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Ir para o login
            </button>
          </div>
        ) : checking ? (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-12 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-background-50 rounded-2xl border border-background-200/70 p-6"
          >
            <div className="flex flex-col gap-5">
              <div>
                <label
                  htmlFor="reset-password"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Nova senha
                </label>
                <div className="relative">
                  <input
                    id="reset-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors"
                    tabIndex={-1}
                  >
                    <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-lg`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="reset-confirm"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Confirmar nova senha
                </label>
                <input
                  id="reset-confirm"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a nova senha"
                  className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? 'Redefinindo...' : 'Redefinir senha'}
              </button>
            </div>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-foreground-600">
          <button
            onClick={() => navigate('/login')}
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors inline-flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Voltar para o login
          </button>
        </p>
      </div>
    </div>
  );
}
